import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

export default function SearchConsoleLab() {
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [dims, setDims] = useState(['query']);
  const [limit, setLimit] = useState(50);
  const [startRow, setStartRow] = useState(0);
  const [pageFilter, setPageFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const baseHeaders = { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) };

  const loadSites = async () => {
    try {
      setSitesLoading(true);
      setSites([]);
      setSelectedSite('');
      const res = await fetch('https://api.glowytics.com/api/auth/search-console/sites', {
        credentials: 'include',
        headers: baseHeaders
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Failed to load sites';
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      const entries = Array.isArray(data?.siteEntry) ? data.siteEntry : [];
      setSites(entries);
      if (entries.length) setSelectedSite(entries[0].siteUrl);
      toast.success(`Loaded ${entries.length} site(s)`);
    } catch (e) {
      console.error('SC sites error:', e);
      toast.error(e.message || 'Failed to load sites');
    } finally {
      setSitesLoading(false);
    }
  };

  const runQuery = async (overrideDims) => {
    try {
      if (new Date(startDate) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
      setLoading(true);
      setResult(null);
      const payload = {
        startDate,
        endDate,
        dimensions: overrideDims || dims,
        rowLimit: Number(limit) || 50,
        startRow: Number(startRow) || 0
      };
      if (selectedSite) payload.siteUrl = selectedSite; // fallback to connection default if not provided
      if (pageFilter && (overrideDims || dims).includes('page')) {
        payload.dimensionFilterGroups = [{
          filters: [{ dimension: 'page', operator: 'contains', expression: pageFilter }]
        }];
      }
      const res = await fetch('https://api.glowytics.com/api/labs/search-console/query', {
        method: 'POST',
        credentials: 'include',
        headers: baseHeaders,
        body: JSON.stringify(payload)
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Query failed';
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || js.details || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setResult(data);
      toast.success('Query OK');
    } catch (e) {
      console.error('SC query error:', e);
      toast.error(e.message || 'Query failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleDim = (d) => {
    setDims(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  // Helper render for rows
  const renderRows = () => {
    const rows = Array.isArray(result?.rows) ? result.rows : [];
    if (!rows.length) {
      return <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">{JSON.stringify(result, null, 2)}</pre>;
    }
    const activeDims = dims;
    const hasDate = activeDims.includes('date');
    return (
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {activeDims.map((d, i) => (<th key={i} className="text-left p-2 capitalize">{d}</th>))}
              <th className="text-left p-2">Clicks</th>
              <th className="text-left p-2">Impressions</th>
              <th className="text-left p-2">CTR</th>
              <th className="text-left p-2">Position</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const keys = Array.isArray(r.keys) ? r.keys : [];
              return (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {activeDims.map((d, i) => (
                    <td key={i} className="p-2 whitespace-nowrap">
                      {String(keys[i] ?? '')}
                    </td>
                  ))}
                  <td className="p-2">{r.clicks}</td>
                  <td className="p-2">{r.impressions}</td>
                  <td className="p-2">{(r.ctr * 100).toFixed(2)}%</td>
                  <td className="p-2">{Number(r.position).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold mb-4">Labs · Search Console Tester</h1>

      <div className="mb-4 p-4 border rounded-lg bg-white">
        <h2 className="text-lg font-semibold mb-2">1) Select Site & Query API</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
            <div className="flex gap-2">
              <select
                className="min-w-[280px] border rounded-lg p-2"
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
              >
                <option value="">{sites.length ? 'Select a site' : 'No sites loaded'}</option>
                {sites.map(s => (
                  <option key={s.siteUrl} value={s.siteUrl}>{s.siteUrl}</option>
                ))}
              </select>
              <button
                onClick={loadSites}
                disabled={sitesLoading}
                className={`px-3 py-2 rounded-lg font-medium ${sitesLoading ? 'bg-gray-300 text-gray-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
              >
                {sitesLoading ? 'Loading…' : 'Load Sites'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Not selected = will use default Search Console site from your connection.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input type="date" className="border rounded-lg p-2" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input type="date" className="border rounded-lg p-2" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
            <input type="number" className="w-28 border rounded-lg p-2" value={limit} onChange={(e) => setLimit(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Row</label>
            <input type="number" className="w-28 border rounded-lg p-2" value={startRow} onChange={(e) => setStartRow(e.target.value)} />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-4 items-center">
          <div className="flex flex-wrap gap-3">
            {['query', 'page', 'country', 'device', 'date'].map(d => (
              <label key={d} className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" className="w-4 h-4" checked={dims.includes(d)} onChange={() => toggleDim(d)} />
                <span className="capitalize">{d}</span>
              </label>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page filter (contains)</label>
              <input className="min-w-[260px] border rounded-lg p-2" value={pageFilter} onChange={(e) => setPageFilter(e.target.value)} placeholder="/collection/" />
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => runQuery()}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {loading ? 'Running…' : 'Run Query'}
          </button>
          <button
            onClick={() => runQuery(['query'])}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
          >
            Top Queries
          </button>
          <button
            onClick={() => runQuery(['page'])}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
          >
            Pages
          </button>
          <button
            onClick={() => runQuery(['country'])}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
          >
            Countries
          </button>
          <button
            onClick={() => runQuery(['device'])}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
          >
            Devices
          </button>
          <button
            onClick={() => runQuery(['date'])}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-slate-700 text-white hover:bg-slate-800'}`}
          >
            Daily Totals
          </button>
        </div>
      </div>

      <div className="mb-4 p-4 border rounded-lg bg-white">
        <h2 className="text-lg font-semibold mb-2">2) Write to DB (Daily Tables)</h2>
        <p className="text-sm text-gray-600 mb-3">Date range is required. If no site is selected, default site from your connection will be used.</p>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'query', label: 'Write Query Daily' },
            { key: 'page', label: 'Write Page Daily' },
            { key: 'country', label: 'Write Country Daily' },
            { key: 'device', label: 'Write Device Daily' },
            { key: 'totals', label: 'Write Totals Daily' },
          ].map(btn => (
            <button
              key={btn.key}
              onClick={async () => {
                try {
                  if (new Date(startDate) > new Date(endDate)) {
                    toast.error('Start date cannot be after end date');
                    return;
                  }
                  setLoading(true);
                  const res = await fetch(`https://api.glowytics.com/api/labs/search-console/ingest/${btn.key}`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: baseHeaders,
                    body: JSON.stringify(
                      selectedSite
                        ? { siteUrl: selectedSite, startDate, endDate }
                        : { startDate, endDate } // backend will fallback to connection site
                    )
                  });
                  const ct = res.headers.get('content-type') || '';
                  if (!res.ok) {
                    let msg = 'Ingest failed';
                    if (ct.includes('application/json')) {
                      const js = await res.json().catch(() => ({}));
                      msg = js.error || js.details || msg;
                    }
                    throw new Error(msg);
                  }
                  const data = await res.json();
                  toast.success(`${btn.label} OK — inserted: ${data.inserted ?? 0}`);
                } catch (e) {
                  console.error('Ingest error:', e);
                  toast.error(e.message || 'Ingest failed');
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {result && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-2">Result</h2>
          {renderRows()}
        </div>
      )}
    </div>
  );
}


